/**
 * Token reconciliation for {{placeholder}} authoring.
 *
 * Both the contract and the email editor used to ask the author to DECLARE
 * tokens in a comma-separated field while USING them freely in the body — two
 * lists that drift apart. Both editors already computed the drift and neither
 * blocked on it. This closes the loop: declaration becomes a consequence of
 * use, and the drift becomes a publish blocker.
 */

export type TokenState = 'declared' | 'auto' | 'undeclared' | 'unused';

export interface TokenInfo {
  name: string;
  state: TokenState;
  /** Occurrences in the body. 0 for `unused`. */
  count: number;
}

/** Presentation per state. Tokens, so the chips track the palette. */
export const TOKEN_STATE_META: Record<
  TokenState,
  { label: string; tone: string; tint: string; line: string; help: string }
> = {
  declared: {
    label: 'Declared',
    tone: 'var(--acc)',
    tint: 'var(--acc-tint)',
    line: 'var(--acc-line)',
    help: 'Declared and used. You supply the value when sending.',
  },
  auto: {
    label: 'Auto-filled',
    tone: 'var(--teal)',
    tint: 'var(--teal-tint)',
    line: 'rgba(47, 179, 168, 0.40)',
    help: 'Resolved server-side at send time. Do not declare these.',
  },
  undeclared: {
    label: 'Undeclared',
    tone: 'var(--red)',
    tint: 'var(--red-tint)',
    line: 'rgba(236, 132, 128, 0.40)',
    help: 'Used in the body but never declared, so it would render empty. Blocks publish.',
  },
  unused: {
    label: 'Unused',
    tone: 'var(--grey)',
    tint: 'var(--grey-tint)',
    line: 'rgba(150, 160, 177, 0.40)',
    help: 'Declared but absent from the body. Safe to remove.',
  },
};

/** Matches `{{ name }}` with optional inner whitespace. */
export const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Every token used in `body`, with an occurrence count, in first-seen order. */
export function parseTokens(body: string): Map<string, number> {
  const found = new Map<string, number>();
  const re = new RegExp(TOKEN_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    found.set(m[1], (found.get(m[1]) ?? 0) + 1);
  }
  return found;
}

/**
 * Reconcile what the body uses against what has been declared.
 *
 * @param body          the authored text
 * @param declared      names in availableVariables
 * @param autoVars      names the server resolves itself; never declared
 */
export function classifyTokens(
  body: string,
  declared: string[],
  autoVars: string[],
): TokenInfo[] {
  const used = parseTokens(body);
  const declaredSet = new Set(declared);
  const autoSet = new Set(autoVars);
  const out: TokenInfo[] = [];

  for (const [name, count] of used) {
    if (autoSet.has(name)) out.push({ name, state: 'auto', count });
    else if (declaredSet.has(name)) out.push({ name, state: 'declared', count });
    else out.push({ name, state: 'undeclared', count });
  }
  // Declared but never used — safe to remove, so surface it rather than leaving
  // it to accumulate silently.
  for (const name of declared) {
    if (!used.has(name) && !autoSet.has(name)) out.push({ name, state: 'unused', count: 0 });
  }
  return out;
}

/** Names the author must declare for the body to be complete. */
export function reconcileDeclared(body: string, declared: string[], autoVars: string[]): string[] {
  const used = parseTokens(body);
  const autoSet = new Set(autoVars);
  const next = new Set(declared.filter((d) => !autoSet.has(d)));
  for (const name of used.keys()) if (!autoSet.has(name)) next.add(name);
  return Array.from(next);
}

/**
 * Rename a token everywhere it appears.
 *
 * Renaming from the chip used to silently orphan every occurrence in the body,
 * because only the declaration list changed.
 */
export function renameToken(body: string, from: string, to: string): string {
  const re = new RegExp(`\\{\\{\\s*${from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}\\}`, 'g');
  return body.replace(re, `{{${to}}}`);
}

/** Remove every occurrence of a token from the body. */
export function removeToken(body: string, name: string): string {
  const re = new RegExp(`\\{\\{\\s*${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}\\}`, 'g');
  return body.replace(re, '');
}

/** A token name is valid if it matches what the parser can find again. */
export function isValidTokenName(name: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(name);
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Resolve tokens for preview.
 *
 * A token with no sample value renders highlighted amber inline rather than as
 * blank space: blank space is indistinguishable from intentional whitespace,
 * so the author cannot see what they have not filled in.
 */
export function resolvePreview(
  body: string,
  values: Record<string, string>,
  options: { markUnfilled?: boolean } = {},
): string {
  const { markUnfilled = true } = options;
  return body.replace(new RegExp(TOKEN_RE.source, 'g'), (_full, name: string) => {
    const value = values[name];
    if (value !== undefined && value !== '') return escapeHtml(value);
    if (!markUnfilled) return '';
    return (
      `<span style="background:var(--amber-tint);color:var(--amber);` +
      `border-radius:4px;padding:0 4px;font-family:var(--font-family-mono);` +
      `font-size:0.9em;">${escapeHtml(name)}</span>`
    );
  });
}

/**
 * Insert `{{name}}` at the cursor of a textarea/input, replacing any selection
 * and leaving the caret after the inserted token.
 *
 * Returns the next value; the caller owns the state, and restores the caret via
 * the returned selection index.
 */
export function insertTokenAtCursor(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  name: string,
): { value: string; caret: number } {
  const token = `{{${name}}}`;
  if (!el) return { value: value + token, caret: value.length + token.length };
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? start;
  const next = value.slice(0, start) + token + value.slice(end);
  return { value: next, caret: start + token.length };
}

/**
 * Detect an open `{{` immediately before the caret so the picker can appear
 * inline — authors never hand-type brace syntax.
 *
 * Returns the partial name typed after the braces, or null when the caret is
 * not inside an unclosed `{{`.
 */
export function detectOpenBraces(value: string, caret: number): { query: string; start: number } | null {
  const before = value.slice(0, caret);
  const open = before.lastIndexOf('{{');
  if (open === -1) return null;
  const between = before.slice(open + 2);
  // Already closed, or ran past what a token name may contain.
  if (between.includes('}') || /[^a-zA-Z0-9_]/.test(between)) return null;
  return { query: between, start: open };
}

/** Replace the in-progress `{{partial` at the caret with a complete token. */
export function completeOpenBraces(
  value: string,
  caret: number,
  start: number,
  name: string,
): { value: string; caret: number } {
  const token = `{{${name}}}`;
  const next = value.slice(0, start) + token + value.slice(caret);
  return { value: next, caret: start + token.length };
}
